import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import s3Service from '../services/s3Service';
import { uploadFileToS3WithProgress } from '../services/s3Service';
import styles from '../assets/ticketDocumentCreate.module.css';
import { saveDraft, isQuotaError } from '../lib/offlineDb';
import { triggerSync } from '../lib/inspectionSyncEngine';

const OFFLINE_MAX_SIZE = 25 * 1024 * 1024; // 25MB cap for offline buffering
const genUuid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });

// Supported file formats matching Laravel
const SUPPORTED_FORMATS = {
  documents: ['pdf', 'doc', 'docx', 'txt', 'rtf'],
  images: ['jpg', 'jpeg', 'png', 'gif', 'bmp'],
  videos: ['mp4', 'mov', 'avi', 'wmv', 'flv', 'webm', 'm4v', '3gp']
};

const ACCEPTED_EXTENSIONS = [
  ...SUPPORTED_FORMATS.documents.map(ext => `.${ext}`),
  ...SUPPORTED_FORMATS.images.map(ext => `.${ext}`),
  ...SUPPORTED_FORMATS.videos.map(ext => `.${ext}`)
].join(',');

const MAX_FILE_SIZE_MB = 512;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const TicketDocumentCreate = () => {
  const { id: ticketId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]); // raw File objects (for offline buffering)
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Validate file sizes
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`File "${file.name}" exceeds maximum size of ${MAX_FILE_SIZE_MB}MB`);
        e.target.value = '';
        return;
      }
    }

    // Keep the raw files so we can buffer them if offline / on submit failure.
    setSelectedFiles(files);

    // Offline: don't try to reach S3 now — they'll be buffered and synced on submit.
    const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    if (!isOnline) {
      setUploadedFiles([]);
      toast.info(`${files.length} file(s) ready — they’ll upload when you submit (offline).`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setTotalFiles(files.length);
    const uploaded = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setCurrentFileIndex(i + 1);

        // Get presigned URL
        const { url: presignedUrl, key } = await s3Service.getPresignedUrl(
          file.name,
          file.type,
          'documents'
        );

        // Upload using presigned URL with progress
        await uploadFileToS3WithProgress(file, presignedUrl, (progress) => {
          setUploadProgress(progress);
        });

        // Store uploaded file info
        uploaded.push({
          key,
          name: file.name,
          size: file.size,
          type: file.type
        });
      }

      setUploadedFiles(uploaded);
      toast.success(`${uploaded.length} file(s) uploaded successfully`);
    } catch (error) {
      console.error('Error uploading file:', error);
      // Network/S3 failure — keep the selected files and buffer them on submit.
      setUploadedFiles([]);
      toast.info('Upload will be completed automatically when you submit.');
    } finally {
      setUploading(false);
      setCurrentFileIndex(0);
      setTotalFiles(0);
    }
  };

  // Queue the documents for background sync (offline, or online submit failed).
  // Returns true if queued.
  const queueDocumentsOffline = async () => {
    if (!selectedFiles.length) return false;
    const allUploaded = uploadedFiles.length === selectedFiles.length;

    // Files that still need buffering (no S3 key yet) must respect the offline cap.
    if (!allUploaded) {
      const tooBig = selectedFiles.find((f) => f.size > OFFLINE_MAX_SIZE);
      if (tooBig) {
        toast.error(
          `While offline, files must be under ${OFFLINE_MAX_SIZE / (1024 * 1024)}MB. Upload larger files when you’re back online.`
        );
        return false;
      }
    }

    try {
      const photos = {};
      selectedFiles.forEach((file, i) => {
        const id = genUuid();
        const up = allUploaded ? uploadedFiles[i] : null;
        photos[id] = up && up.key
          ? {
              s3Key: up.key, // already on S3 — sync skips re-upload
              fileName: file.name,
              mimeType: file.type,
              size: file.size,
              folder: 'documents',
              clientUuid: genUuid(),
            }
          : {
              blob: file,
              fileName: file.name,
              mimeType: file.type,
              size: file.size,
              folder: 'documents',
              clientUuid: genUuid(),
            };
      });

      await saveDraft({
        id: genUuid(),
        ticketId: String(ticketId),
        reportType: 'ticketDocument',
        name,
        photos,
        status: 'queued',
        attempts: 0,
        permanent: false,
        lastError: null,
      });
    } catch (err) {
      toast.error(
        isQuotaError(err)
          ? 'This device is out of storage, so the documents could not be saved for offline upload.'
          : 'Offline storage is unavailable, so the documents cannot be saved while offline.'
      );
      return false;
    }

    toast.info('Saved. Your documents will upload automatically when you’re back online.');
    triggerSync();
    navigate(`/tickets/${ticketId}`);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFiles.length) {
      toast.error('Please select at least one file');
      return;
    }

    setLoading(true);

    const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    const allUploaded = uploadedFiles.length === selectedFiles.length;

    // Offline, or files weren't uploaded on select → queue for background sync.
    if (!isOnline || !allUploaded) {
      const queued = await queueDocumentsOffline();
      if (!queued) setLoading(false);
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      // Save each uploaded file as a document
      for (const uploadedFile of uploadedFiles) {
        const response = await fetch(`${baseUrl}/api/tickets/${ticketId}/documents`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            name: name || uploadedFile.name,
            key: uploadedFile.key,
            file_name: uploadedFile.name,
            file_type: uploadedFile.type,
            file_size: uploadedFile.size,
            folder: 'documents'
          })
        });

        if (!response.ok) {
          throw new Error('Failed to save document');
        }
      }

      toast.success(`${uploadedFiles.length} document(s) added successfully!`);
      navigate(`/tickets/${ticketId}`);
    } catch (error) {
      console.error('Error saving document:', error);
      // Network failure → fall back to the offline queue instead of losing them.
      const queued = await queueDocumentsOffline();
      if (queued) return;
      toast.error('Failed to add document');
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <span onClick={() => navigate('/dashboard')}>Dashboard</span>
        <span> / </span>
        <span onClick={() => navigate('/tickets')}>Tickets</span>
        <span> / </span>
        <span>Document</span>
        <span> / </span>
        <span>Create</span>
      </div>

      <h1 className={styles.title}>Add document to ticket</h1>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label>Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={styles.input}
            placeholder="You can leave it blank"
          />
        </div>

        <div className={styles.formGroup}>
          <label>Documents (may choose multiple) <span className={styles.required}>*</span></label>
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            className={styles.fileInput}
            disabled={uploading}
            accept={ACCEPTED_EXTENSIONS}
          />
          <div className={styles.formatInfo}>
            <strong>Supported formats:</strong> Documents ({SUPPORTED_FORMATS.documents.map(f => f.toUpperCase()).join(', ')}),
            Images ({SUPPORTED_FORMATS.images.map(f => f.toUpperCase()).join(', ')}),
            Videos ({SUPPORTED_FORMATS.videos.map(f => f.toUpperCase()).join(', ')})
          </div>
          <div className={styles.formatInfo}>
            <strong>Maximum file size:</strong> {MAX_FILE_SIZE_MB}MB per file
          </div>
          {uploading && (
            <div className={styles.uploadingText}>
              Uploading file {currentFileIndex} of {totalFiles}... {uploadProgress}%
            </div>
          )}
          {uploadedFiles.length > 0 && (
            <div className={styles.uploadedList}>
              {uploadedFiles.map((file, index) => (
                <div key={index} className={styles.uploadedText}>
                  ✓ {file.name}
                </div>
              ))}
            </div>
          )}
          {uploadedFiles.length === 0 && selectedFiles.length > 0 && (
            <div className={styles.uploadedList}>
              {selectedFiles.map((file, index) => (
                <div key={index} className={styles.uploadedText}>
                  📄 {file.name} (will upload on submit)
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          type="submit"
          className={styles.submitButton}
          disabled={loading || uploading || !selectedFiles.length}
        >
          {loading ? 'Adding...' : '+ Add document'}
        </button>
      </form>

      <div className={styles.copyright}>
        Copyright 2025 Global Guardians. All rights reserved
      </div>
    </div>
  );
};

export default TicketDocumentCreate;